import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import VehiclesPageOptimized from './VehiclesPageOptimized';
import VehiclesPageSimple from './VehiclesPageSimple';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  LinearProgress,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
} from '@mui/material';
import {
  Search,
  FilterList,
  MoreVert,
  DirectionsCar,
  CheckCircle,
  Warning,
  Error,
  Add,
  Edit,
  Visibility,
  Download,
  TrendingUp,
  TrendingDown,
  ExpandMore,
  Schedule,
  CameraAlt,
  Dashboard,
  Assignment,
  VerifiedUser,
  ListAlt,
  Check,
  Close,
  ViewList,
  ViewModule,
  CarRepair,
  RateReview,
  Upload,
  Person,
  CalendarToday,
  Star,
} from '@mui/icons-material';

interface Vehicle {
  id: string;
  vin: string;
  make: string;
  model: string;
  year: number;
  ownerType: 'Owner' | 'Broker' | 'Liaison' | 'Fleet';
  status: 'Active' | 'Pending' | 'Suspended';
  verificationScore: number;
  qualityScore: number;
  lastInspection: string;
  owner: {
    id: string;
    name: string;
    phone: string;
    email: string;
    avatar?: string;
    profileImage?: string;
    joinedDate?: string;
    totalVehicles?: number;
    rating?: number;
  };
  documents: {
    insurance: boolean;
    registration: boolean;
    inspection: boolean;
    gpsConfirmed: boolean;
  };
  photos: {
    front: boolean;
    back: boolean;
    side: boolean;
    interior: boolean;
    dashboard: boolean;
  };
  bookingHistory: {
    totalBookings: number;
    completed: number;
    cancelled: number;
    avgRating: number;
  };
  substitutionLogs: {
    totalSubstitutions: number;
    lastSubstitution: string;
    substitutionRate: number;
  };
}

export const FleetManagementHub: React.FC = () => {
  const navigate = useNavigate();
  
  // Host profile states  
  const [showHostProfile, setShowHostProfile] = useState(false);
  const [selectedHostId, setSelectedHostId] = useState<string | null>(null);

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Enhanced Header with Quick Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: 3, pt: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, color: '#1a1a1a' }}>
            Fleet Management Hub
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Comprehensive fleet operations: vehicles, hosts, quality control, approvals & analytics
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Upload />}
            onClick={() => window.scrollTo({ top: 800, behavior: 'smooth' })}
            sx={{ textTransform: 'none' }}
          >
            Bulk Upload
          </Button>
          <Button
            variant="outlined"
            startIcon={<RateReview />}
            onClick={() => navigate('/approvals')}
            sx={{ textTransform: 'none' }}
          >
            Approvals
          </Button>
          <Button
            variant="contained"
            startIcon={<CarRepair />}
            onClick={() => window.scrollTo({ top: 800, behavior: 'smooth' })}
            sx={{ textTransform: 'none', bgcolor: '#2e3440', '&:hover': { bgcolor: '#3b4252' } }}
          >
            Manage Fleet
          </Button>
        </Box>
      </Box>

      {/* Quick Actions Cards */}
      <Grid container spacing={3} sx={{ mb: 3, px: 3 }}>
        <Grid item xs={12} md={4}>
          <Card 
            sx={{ 
              bgcolor: 'white', 
              borderRadius: 2, 
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              transition: 'transform 0.2s, boxShadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
              }
            }}
            onClick={() => window.scrollTo({ top: 800, behavior: 'smooth' })}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: '#e3f2fd', color: '#1976d2', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                <CarRepair sx={{ fontSize: 30 }} />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Vehicle Management
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add, edit, and manage individual vehicles with VIN lookup and bulk upload
              </Typography>
              <Button 
                variant="contained" 
                size="small"
                sx={{ textTransform: 'none' }}
              >
                Manage Vehicles
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card 
            sx={{ 
              bgcolor: 'white', 
              borderRadius: 2, 
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              transition: 'transform 0.2s, boxShadow 0.2s',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
              }
            }}
            onClick={() => navigate('/approvals')}
          >
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                <RateReview sx={{ fontSize: 30 }} />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Approvals & Reviews
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Review and approve vehicle listings and driver registrations from mobile app
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
                <Chip 
                  label="15 Pending" 
                  color="warning" 
                  size="small"
                  sx={{ fontSize: '0.75rem' }}
                />
              </Box>
              <Button 
                variant="contained" 
                size="small"
                color="secondary"
                sx={{ textTransform: 'none' }}
              >
                Review Pending
              </Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: 3, textAlign: 'center' }}>
              <Avatar sx={{ bgcolor: '#e8f5e8', color: '#388e3c', mx: 'auto', mb: 2, width: 56, height: 56 }}>
                <VerifiedUser sx={{ fontSize: 30 }} />
              </Avatar>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Quality Control
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Monitor verification scores, photo quality, and documentation completeness
              </Typography>
              <Button 
                variant="contained" 
                size="small"
                color="success"
                sx={{ textTransform: 'none' }}
                disabled
              >
                View Analytics
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Vehicle Operations Content */}
      <VehiclesPageOptimized />
    </Box>
  );
};

const VehiclesPageOld: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedVehicle, setSelectedVehicle] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
  const [expandedDetailView, setExpandedDetailView] = useState(false);

  // Mock data matching the Figma design
  const vehicles: Vehicle[] = [
    {
      id: '1',
      vin: '7FRKN3G5RMG134752',
      make: 'Toyota',
      model: 'Camry',
      year: 2023,
      ownerType: 'Owner',
      status: 'Active',
      verificationScore: 85,
      qualityScore: 90,
      lastInspection: 'Jan 12, 2025',
      owner: { name: 'John Doe', phone: '+1-555-0123' },
      documents: {
        insurance: true,
        registration: true,
        inspection: true,
        gpsConfirmed: true,
      },
      photos: {
        front: true,
        back: true,
        side: true,
        interior: true,
        dashboard: false,
      },
      bookingHistory: {
        totalBookings: 24,
        completed: 21,
        cancelled: 3,
        avgRating: 4.7,
      },
      substitutionLogs: {
        totalSubstitutions: 2,
        lastSubstitution: 'Feb 12, 2025',
        substitutionRate: 8.3,
      },
    },
    {
      id: '2',
      vin: 'WBASR7C5LFH39672',
      make: 'BMW',
      model: 'X5',
      year: 2022,
      ownerType: 'Broker',
      status: 'Active',
      verificationScore: 78,
      qualityScore: 85,
      lastInspection: 'Feb 03, 2025',
      owner: { name: 'Sarah Smith', phone: '+1-555-0124' },
      documents: {
        insurance: true,
        registration: true,
        inspection: true,
        gpsConfirmed: true,
      },
      photos: {
        front: true,
        back: true,
        side: true,
        interior: true,
        dashboard: true,
      },
      bookingHistory: {
        totalBookings: 31,
        completed: 28,
        cancelled: 3,
        avgRating: 4.8,
      },
      substitutionLogs: {
        totalSubstitutions: 1,
        lastSubstitution: 'Jan 15, 2025',
        substitutionRate: 3.2,
      },
    },
    {
      id: '3',
      vin: 'WKNDGBB33JF804251',
      make: 'Mercedes',
      model: 'C-Class',
      year: 2024,
      ownerType: 'Liaison',
      status: 'Pending',
      verificationScore: 65,
      qualityScore: 70,
      lastInspection: 'Pending',
      owner: { name: 'Michael Johnson', phone: '+1-555-0125' },
      documents: {
        insurance: true,
        registration: true,
        inspection: false,
        gpsConfirmed: false,
      },
      photos: {
        front: true,
        back: true,
        side: false,
        interior: true,
        dashboard: false,
      },
      bookingHistory: {
        totalBookings: 12,
        completed: 10,
        cancelled: 2,
        avgRating: 4.5,
      },
      substitutionLogs: {
        totalSubstitutions: 0,
        lastSubstitution: 'N/A',
        substitutionRate: 0,
      },
    },
    {
      id: '4',
      vin: 'SYLXE164AH458321',
      make: 'Audi',
      model: 'A4',
      year: 2023,
      ownerType: 'Fleet',
      status: 'Active',
      verificationScore: 92,
      qualityScore: 88,
      lastInspection: 'Mar 15, 2025',
      owner: { name: 'Fleet Manager', phone: '+1-555-0126' },
      documents: {
        insurance: true,
        registration: true,
        inspection: true,
        gpsConfirmed: true,
      },
      photos: {
        front: true,
        back: true,
        side: true,
        interior: true,
        dashboard: true,
      },
      bookingHistory: {
        totalBookings: 45,
        completed: 42,
        cancelled: 3,
        avgRating: 4.9,
      },
      substitutionLogs: {
        totalSubstitutions: 3,
        lastSubstitution: 'Mar 01, 2025',
        substitutionRate: 6.7,
      },
    },
    {
      id: '5',
      vin: 'THGC3VF25JM612675',
      make: 'Tesla',
      model: 'Model 3',
      year: 2023,
      ownerType: 'Owner',
      status: 'Suspended',
      verificationScore: 45,
      qualityScore: 50,
      lastInspection: 'Oct 28, 2024',
      owner: { name: 'Jennifer White', phone: '+1-555-0127' },
      documents: {
        insurance: false,
        registration: true,
        inspection: false,
        gpsConfirmed: false,
      },
      photos: {
        front: true,
        back: false,
        side: true,
        interior: false,
        dashboard: false,
      },
      bookingHistory: {
        totalBookings: 8,
        completed: 5,
        cancelled: 3,
        avgRating: 3.8,
      },
      substitutionLogs: {
        totalSubstitutions: 5,
        lastSubstitution: 'Feb 28, 2025',
        substitutionRate: 62.5,
      },
    },
  ];

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, vehicleId: string) => {
    setAnchorEl(event.currentTarget);
    setSelectedVehicle(vehicleId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedVehicle(null);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'success';
      case 'Pending': return 'warning';
      case 'Suspended': return 'error';
      default: return 'default';
    }
  };

  const getOwnerTypeColor = (ownerType: string) => {
    switch (ownerType) {
      case 'Owner': return 'primary';
      case 'Broker': return 'secondary';
      case 'Liaison': return 'info';
      case 'Fleet': return 'warning';
      default: return 'default';
    }
  };

  const stats = {
    totalListings: vehicles.length,
    activeListings: vehicles.filter(v => v.status === 'Active').length,
    pendingVerification: vehicles.filter(v => v.status === 'Pending').length,
    suspended: vehicles.filter(v => v.status === 'Suspended').length,
  };

  const qualityMetrics = {
    photoQuality: 76,
    disputeFreePercentage: 92,
    vinPresence: 87,
    documentationCompleteness: 81,
  };

  const inspectionStats = {
    completed: 78,
    pending: 43,
    failed: 12,
  };

  const filteredVehicles = vehicles.filter(vehicle => {
    const matchesSearch = vehicle.vin.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vehicle.make.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vehicle.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         vehicle.ownerType.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Page Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: 3, pt: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, color: '#1a1a1a' }}>
            Vehicle Operations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage vehicle listings, quality, inspections, and visibility impact
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<FilterList />}
            sx={{ textTransform: 'none' }}
          >
            Filter
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            sx={{ textTransform: 'none' }}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ textTransform: 'none', bgcolor: '#2e3440', '&:hover': { bgcolor: '#3b4252' } }}
          >
            Add Vehicle
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3, px: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: '#e8f4f8', color: '#1976d2', mr: 2 }}>
                  <DirectionsCar />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                    {stats.totalListings}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Listings
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUp sx={{ color: 'success.main', fontSize: 16, mr: 0.5 }} />
                <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                  4.8%
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: '#e8f5e8', color: '#388e3c', mr: 2 }}>
                  <CheckCircle />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                    {stats.activeListings}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active Listings
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUp sx={{ color: 'success.main', fontSize: 16, mr: 0.5 }} />
                <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                  2.5%
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: '#fff3e0', color: '#f57c00', mr: 2 }}>
                  <Schedule />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                    {stats.pendingVerification}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending Verification
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUp sx={{ color: 'success.main', fontSize: 16, mr: 0.5 }} />
                <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                  12%
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ bgcolor: '#ffebee', color: '#d32f2f', mr: 2 }}>
                  <Error />
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                    {stats.suspended}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Suspended
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  0%
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Vehicle Listings Table */}
      <Box sx={{ px: 3, mb: 3 }}>
        <Card sx={{ bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: 0 }}>
            {/* Search and Controls */}
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
                Vehicle Listings
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  placeholder="Search listings..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  sx={{ width: 300 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                />
                <IconButton size="small" onClick={() => setViewMode(viewMode === 'list' ? 'grid' : 'list')}>
                  {viewMode === 'list' ? <ViewModule /> : <ViewList />}
                </IconButton>
              </Box>
            </Box>

            {/* Table */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, color: '#666' }}>Vehicle Info</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#666' }}>Owner Type</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#666' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#666' }}>Verification Score</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#666' }}>Quality Score</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#666' }}>Last Inspection</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#666' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredVehicles
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((vehicle) => (
                      <TableRow key={vehicle.id} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: '#f5f5f5', color: '#666' }}>
                              <DirectionsCar />
                            </Avatar>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                VIN: {vehicle.vin}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {vehicle.make} {vehicle.model} ({vehicle.year})
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={vehicle.ownerType}
                            size="small"
                            color={getOwnerTypeColor(vehicle.ownerType) as any}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={vehicle.status}
                            size="small"
                            color={getStatusColor(vehicle.status) as any}
                            variant="filled"
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={vehicle.verificationScore}
                              sx={{ 
                                width: 80, 
                                height: 6, 
                                borderRadius: 3,
                                bgcolor: '#e0e0e0',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: vehicle.verificationScore >= 80 ? '#4caf50' : vehicle.verificationScore >= 60 ? '#ff9800' : '#f44336'
                                }
                              }}
                            />
                            <Typography variant="caption" sx={{ minWidth: 35 }}>
                              {vehicle.verificationScore}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={vehicle.qualityScore}
                              sx={{ 
                                width: 80, 
                                height: 6, 
                                borderRadius: 3,
                                bgcolor: '#e0e0e0',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: vehicle.qualityScore >= 80 ? '#4caf50' : vehicle.qualityScore >= 60 ? '#ff9800' : '#f44336'
                                }
                              }}
                            />
                            <Typography variant="caption" sx={{ minWidth: 35 }}>
                              {vehicle.qualityScore}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color={vehicle.lastInspection === 'Pending' ? 'warning.main' : 'text.primary'}>
                            {vehicle.lastInspection}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <Tooltip title="View Details">
                              <IconButton size="small">
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton size="small">
                                <Edit fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <IconButton
                              size="small"
                              onClick={(e) => handleMenuOpen(e, vehicle.id)}
                            >
                              <MoreVert fontSize="small" />
                            </IconButton>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Showing {page * rowsPerPage + 1} of {filteredVehicles.length} vehicles
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  disabled={page === 0}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  sx={{ bgcolor: '#2e3440', minWidth: 32, '&:hover': { bgcolor: '#3b4252' } }}
                >
                  {page + 1}
                </Button>
                <Button size="small" variant="outlined">2</Button>
                <Button size="small" variant="outlined">3</Button>
                <Button
                  size="small"
                  disabled={page >= Math.ceil(filteredVehicles.length / rowsPerPage) - 1}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Bottom Section */}
      <Grid container spacing={3} sx={{ px: 3, pb: 3 }}>
        {/* Quality Score Metrics */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Quality Score Metrics
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Photo Quality</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {qualityMetrics.photoQuality}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={qualityMetrics.photoQuality}
                    sx={{ 
                      height: 6, 
                      borderRadius: 3,
                      bgcolor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': { bgcolor: '#4caf50' }
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Based on resolution, clarity, and coverage of vehicle
                  </Typography>
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Dispute-free Percentage</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {qualityMetrics.disputeFreePercentage}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={qualityMetrics.disputeFreePercentage}
                    sx={{ 
                      height: 6, 
                      borderRadius: 3,
                      bgcolor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': { bgcolor: '#4caf50' }
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Percentage of bookings without customer disputes
                  </Typography>
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">VIN Presence</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {qualityMetrics.vinPresence}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={qualityMetrics.vinPresence}
                    sx={{ 
                      height: 6, 
                      borderRadius: 3,
                      bgcolor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': { bgcolor: '#4caf50' }
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Percentage of listings with verified VIN numbers
                  </Typography>
                </Box>
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Documentation Completeness</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {qualityMetrics.documentationCompleteness}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={qualityMetrics.documentationCompleteness}
                    sx={{ 
                      height: 6, 
                      borderRadius: 3,
                      bgcolor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': { bgcolor: '#4caf50' }
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Percentage of listings with complete documentation
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Inspection Management */}
        <Grid item xs={12} md={6}>
          <Card sx={{ bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Inspection Management
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  sx={{ bgcolor: '#2e3440', textTransform: 'none', '&:hover': { bgcolor: '#3b4252' } }}
                >
                  Schedule New
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Completed Inspections (Last 30 days)</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {inspectionStats.completed}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Pending Inspections</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {inspectionStats.pending}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2">Failed Inspections</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {inspectionStats.failed}
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                  Auto-Badge Logic
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <FormControlLabel
                    control={<Checkbox defaultChecked size="small" />}
                    label="Inspection within last 6 months"
                  />
                  <FormControlLabel
                    control={<Checkbox defaultChecked size="small" />}
                    label="Matched VIN"
                  />
                  <FormControlLabel
                    control={<Checkbox defaultChecked size="small" />}
                    label="GPS Confirmed"
                  />
                  <FormControlLabel
                    control={<Checkbox size="small" />}
                    label="Professional photos"
                  />
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{ mt: 2, textTransform: 'none' }}
                >
                  Update Badge Logic
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Vehicle Detail View */}
      <Box sx={{ px: 3, pb: 3 }}>
        <Card sx={{ bgcolor: 'white', borderRadius: 2, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
          <Accordion 
            expanded={expandedDetailView} 
            onChange={() => setExpandedDetailView(!expandedDetailView)}
            sx={{ boxShadow: 'none' }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Vehicle Detail View
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                      Photos
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Box sx={{ 
                          bgcolor: '#f5f5f5', 
                          height: 80, 
                          borderRadius: 1, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          flexDirection: 'column'
                        }}>
                          <CameraAlt sx={{ color: '#666', mb: 1 }} />
                          <Typography variant="caption">Front</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ 
                          bgcolor: '#f5f5f5', 
                          height: 80, 
                          borderRadius: 1, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          flexDirection: 'column'
                        }}>
                          <CameraAlt sx={{ color: '#666', mb: 1 }} />
                          <Typography variant="caption">Back</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ 
                          bgcolor: '#f5f5f5', 
                          height: 80, 
                          borderRadius: 1, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          flexDirection: 'column'
                        }}>
                          <CameraAlt sx={{ color: '#666', mb: 1 }} />
                          <Typography variant="caption">Side</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={6}>
                        <Box sx={{ 
                          bgcolor: '#f5f5f5', 
                          height: 80, 
                          borderRadius: 1, 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          flexDirection: 'column'
                        }}>
                          <CameraAlt sx={{ color: '#666', mb: 1 }} />
                          <Typography variant="caption">Interior</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                      Documents
                    </Typography>
                    <List dense>
                      <ListItem>
                        <ListItemText primary="Insurance" />
                        <ListItemSecondaryAction>
                          <CheckCircle sx={{ color: 'success.main' }} />
                        </ListItemSecondaryAction>
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Registration" />
                        <ListItemSecondaryAction>
                          <CheckCircle sx={{ color: 'success.main' }} />
                        </ListItemSecondaryAction>
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="Inspection Report" />
                        <ListItemSecondaryAction>
                          <CheckCircle sx={{ color: 'success.main' }} />
                        </ListItemSecondaryAction>
                      </ListItem>
                      <ListItem>
                        <ListItemText primary="GPS Confirmation" />
                        <ListItemSecondaryAction>
                          <Close sx={{ color: 'error.main' }} />
                        </ListItemSecondaryAction>
                      </ListItem>
                    </List>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                      Booking History
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Total Bookings</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>24</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Completed</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>21</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Cancelled</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>3</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Avg. Rating</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>4.7/5</Typography>
                      </Box>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                      Substitution Logs
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Total Substitutions</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>2</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Last Substitution</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>Feb 12, 2025</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Substitution Rate</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>8.3%</Typography>
                      </Box>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{ mt: 2, textTransform: 'none', width: '100%' }}
                    >
                      View Details
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Card>
      </Box>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 2,
            minWidth: 200,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }
        }}
      >
        <MenuItem onClick={handleMenuClose}>
          <Visibility sx={{ mr: 1 }} /> View Details
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <Edit sx={{ mr: 1 }} /> Edit Vehicle
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <Schedule sx={{ mr: 1 }} /> Schedule Inspection
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <Warning sx={{ mr: 1 }} /> Suspend Vehicle
        </MenuItem>
      </Menu>
    </Box>
  );
};